// Вкладка «Матчи» (Figma «Матч-центр», гид §2.9 «Матч-центр и строка матча»): всё по полотну сервера.
// Вкладки дней — calendar[] (вместе с пустыми датами; открыт день серверного anchor), пустой день — day_empty_state
// с переходом на next_date. Внутри дня — days[].groups[] (порядок, «избранное первым», expanded/collapsible решает
// сервер), строки по match_ids. LIVE и календарь — в серверной шапке (AppBar), состояние общее — matchesUi.
import { useEffect, useMemo, useRef, useState } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { localToday, matchDateChange } from '../analytics/events';
import { track } from '../analytics/track';
import { useMatchesScreen } from '../api/queries';
import { readStart, startKey } from '../api/startCache';
import type { CalendarDay, MatchDay, MatchGroup, MatchRow, MatchesScreen as Screen } from '../api/types';
import caretRight from '../assets/icons/caret-right.svg';
import { formatDate, t } from '../i18n/i18n';
import { paths } from '../nav';
import { haptic } from '../telegram/webapp';
import closeIcon from '../assets/icons/x.svg';
import { MatchRowView, RestoredRowsContext } from '../ui/MatchRowView';
import { AdNewsCard } from '../ui/NewsCardView';
import { adsForDay } from '../ui/newsAds';
import { MaskIcon } from '../ui/MenuCell';
import { useOverlayDismiss } from '../ui/Sheet';
import { ServerTabStrip } from '../ui/ServerTabStrip';
import { EmptyStateView, QueryFallback, StateView } from '../ui/States';
import { useDialogFocus } from '../ui/useDialogFocus';
import { anchorAttr } from '../ui/spotlight/anchors';
import { setMatchesReady, useTourAnchors } from '../tour/launch';
import { setMatchesUi, useMatchesUi } from './matchesUi';
import { PickTeamsCard } from './pickTeams/PickTeamsCard';
import styles from './MatchesScreen.module.css';

// Свёрнутое/раскрытое человеком поверх серверного expanded: ключ «дата:группа».
const toggled = new Map<string, boolean>();

/** ?live=1 (кнопка «Live» из чата бота) включает LIVE один раз: параметр затем снимается с адреса, иначе возврат
 *  на вкладку («Назад» из матча) снова включал бы фильтр поверх выбранного дня (HQ-1737). */
export function liveFromSearch(search: string): boolean {
  return new URLSearchParams(search).get('live') === '1';
}

export function MatchesScreen({ path }: { path: string }) {
  const query = useMatchesScreen(path);
  const ui = useMatchesUi();
  const navigate = useNavigate();
  // ?live=1 — пришли кнопкой «Live» из чата бота (startapp=live)
  const { search } = useLocation();
  useEffect(() => {
    if (!liveFromSearch(search)) return;
    setMatchesUi({ liveOnly: true });
    navigate('/tab/matches', { replace: true });
  }, [search, navigate]);
  // Figma «Скролл вниз»: на этом экране шапка при прокрутке вниз уезжает вместе с таббаром (CSS AppBar по атрибуту)
  useEffect(() => {
    document.documentElement.toggleAttribute('data-collapsing-header', true);
    return () => { document.documentElement.toggleAttribute('data-collapsing-header', false); };
  }, []);
  useRefetchOnReconnect(query.refetch);
  // тур по приложению (HQ-2338) стартует после первой страницы из сети этого запуска — не по кэшу прошлого
  const tourReady = Boolean(query.data) && !query.restored;
  useEffect(() => { setMatchesReady(tourReady); return () => setMatchesReady(false); }, [tourReady]);
  const pickDay = (d: string) => { setMatchesUi({ day: d, liveOnly: false, calendarOpen: false }); window.scrollTo(0, 0); };
  // выбор человека — вкладка дней или календарь — уходит в аналитику; переход «к следующему дню» пустого дня — нет
  const chooseDay = (calendar: CalendarDay[], date: string, source: 'tab' | 'calendar') => (d: string) => {
    const today = calendar.find((c) => c.is_today)?.date ?? localToday();
    const params = matchDateChange(d, date, today, source);
    if (params) track('match_center_date_change', params);
    pickDay(d);
  };
  if (!query.data) {
    // ответа нет — загрузка, повторы после 5xx, ошибка, нет сети, повтор после «Reload» или выбора дня (Figma 772:33139 /
    // 772:33133, HQ-1898 v2): полоса дней видна всё время, меняется только область под ней. Другой день — выбор и повтор
    // запроса полотна (дни — в одном ответе)
    const calendar = offlineCalendar();
    const date = ui.day && calendar.some((c) => c.date === ui.day) ? ui.day : anchorOf(calendar);
    const choose = chooseDay(calendar, date, 'tab');
    return (
      <>
        <DayStrip calendar={calendar} active={date} onSelect={(d) => { choose(d); void query.refetch(); }} />
        <div className={styles.emptyDay} data-failed-day><QueryFallback query={query} compact /></div>
      </>
    );
  }
  const screen = query.data;
  if (screen.empty_state && screen.days.length === 0) {
    return <EmptyStateView state={screen.empty_state} onReload={() => void query.refetch()} />;
  }
  const calendar = calendarOf(screen);
  if (calendar.length > 1) lastCalendar = calendar;
  const anchor = screen.anchor?.date ?? anchorOf(calendar);
  const date = ui.day && calendar.some((c) => c.date === ui.day) ? ui.day : anchor;
  const open = (id: number) => navigate(paths.match(id));
  return (
    <RestoredRowsContext.Provider value={query.restored}>
      {/* в LIVE вкладки дней остаются (Figma «Лайв-матчи»); тап по дню выключает LIVE */}
      <DayStrip calendar={calendar} active={date} onSelect={chooseDay(calendar, date, 'tab')} />
      {/* мягкая карточка выбора любимых (HQ-726) — «Матчи» первый экран Mini App: сессия, избранное пусто, без пропуска */}
      <PickTeamsCard inset />
      {ui.liveOnly
        ? <LiveList screen={screen} field={ui.liveField} onOpen={open} />
        : <DayView screen={screen} day={calendar.find((c) => c.date === date)} onPickDay={pickDay} onOpen={open} />}
      {ui.calendarOpen && <CalendarSheet calendar={calendar} active={date} onPick={chooseDay(calendar, date, 'calendar')} onClose={() => setMatchesUi({ calendarOpen: false })} />}
    </RestoredRowsContext.Provider>
  );
}

/** Сеть вернулась (Figma 772:33136, HQ-2170 AC-3): снекбар уходит, и день перечитывается сразу, а не после staleTime
 *  60 с — иначе после короткого обрыва на экране остаётся счёт до обрыва. Запрос в полёте не перезапускаем. */
function useRefetchOnReconnect(refetch: (options: { cancelRefetch: boolean }) => unknown) {
  const latest = useRef(refetch);
  latest.current = refetch;
  useEffect(() => {
    let online = onlineManager.isOnline();
    return onlineManager.subscribe((next) => {
      if (next && !online) void latest.current({ cancelRefetch: false });
      online = next;
    });
  }, []);
}

/** Вкладки дней — Figma 794:36570: Tab 120×42, подпись прошлого дня на 20 от левого края, справа видно продолжение. */
function DayStrip({ calendar, active, onSelect }: { calendar: CalendarDay[]; active: string; onSelect: (d: string) => void }) {
  if (calendar.length < 2) return null;
  return (
    <div className={styles.bar}>
      <ServerTabStrip tabs={calendar.map((c) => ({ key: c.date, label: c.label }))} active={active} onSelect={onSelect}
        tabWidth={120} align="previous" />
    </div>
  );
}

const anchorOf = (calendar: CalendarDay[]) => calendar.find((c) => c.is_today)?.date ?? calendar[0]?.date ?? '';

// Последнее полученное полотно дней (кэш в памяти): при ошибке сервера и без сети полоса дней не пропадает.
let lastCalendar: CalendarDay[] = [];

const dayShift = (now: Date, days: number) => localToday(new Date(now.getFullYear(), now.getMonth(), now.getDate() + days));

/** Дни для ошибки и «нет сети» (AC-3 HQ-1898): последнее полотно в памяти, иначе кэш холодного старта «Матчей» (не
 *  старше суток), иначе «Yesterday · Today · Tomorrow» по местной дате. В кэше старта хранится только день якоря —
 *  одна вкладка полосы не рисуется, тогда тоже три дня. */
export function offlineCalendar(now = new Date()): CalendarDay[] {
  if (lastCalendar.length > 1) return lastCalendar;
  const saved = readStart<Screen>(startKey('matches'), now.getTime());
  const stored = saved ? calendarOf(saved.data) : [];
  if (stored.length > 1) return stored;
  return (['yesterday', 'today', 'tomorrow'] as const).map((key, i) => ({
    date: dayShift(now, i - 1), label: t(`mc_${key}`), label_key: key, is_today: i === 1, match_count: 0, live_count: 0,
  }));
}

/** Полотно дней; в старом ответе без calendar[] — из days[]. */
function calendarOf(screen: Screen): CalendarDay[] {
  return screen.calendar?.length ? screen.calendar : screen.days.map((d) => ({
    date: d.date, label: d.label, label_key: d.label_key, is_today: d.is_today, match_count: d.match_count, live_count: d.live_count,
  }));
}

function DayView({ screen, day, onPickDay, onOpen }: {
  screen: Screen; day: CalendarDay | undefined; onPickDay: (d: string) => void; onOpen: (id: number) => void;
}) {
  const data = screen.days.find((d) => d.date === day?.date);
  const anchors = useTourAnchors();
  // реклама полотна — после блока этого дня (гид §2.9 after_day); вкладки дней у нас по одной — значит, под группами
  const ads = adsForDay(screen.ads, day?.date ?? '').map((ad) => (
    <section key={ad.id} className={`${styles.group} ${styles.ad}`}><AdNewsCard ad={ad} place="matches" /></section>
  ));
  if (!data || !data.matches.length) {
    const empty = screen.day_empty_state;
    const next = day?.next_date;
    return (
      <>
        {/* Figma Placeholder 772:33130: по центру области под полосой дней (до нижней панели), по ширине — страница без 12 */}
        <div className={styles.emptyDay} data-empty-day>
          <StateView art="empty" title={empty?.title ?? t('mc_empty_day')} text={empty?.text} compact>
            {next && empty?.action?.kind === 'show_next_day' && (
              <button className={styles.nextDay} onClick={() => onPickDay(next)}>{empty.action.label}</button>
            )}
          </StateView>
        </div>
        {ads.length > 0 && <div className={styles.list}>{ads}</div>}
      </>
    );
  }
  return (
    <div className={styles.list}>
      {groupsOf(data).map((g, i) => <GroupCard key={`${data.date}:${groupKey(g)}`} id={`${data.date}:${groupKey(g)}`} group={g} rows={rowsOf(g, data)} onOpen={onOpen}
        anchor={i === 0 && anchors ? FIRST_MATCH : undefined} />)}
      {ads}
    </div>
  );
}

/** LIVE: живые матчи всего полотна, группами сервера (группа с живым матчем — раскрыта). */
function LiveList({ screen, field, onOpen }: { screen: Screen; field: string; onOpen: (id: number) => void }) {
  // фильтр — по полю строки из action.field кнопки шапки (сегодня is_live)
  const groups = useMemo(() => screen.days.flatMap((day) => groupsOf(day)
    .map((g) => ({ day, group: g, rows: rowsOf(g, day).filter((m) => Boolean((m as unknown as Record<string, unknown>)[field])) }))
    .filter((x) => x.rows.length > 0)), [screen.days, field]);
  if (!groups.length) return <StateView art="empty" title={t('tg_no_live')} />;
  return (
    <div className={styles.list}>
      {groups.map(({ day, group, rows }) => (
        <GroupCard key={`live:${day.date}:${groupKey(group)}`} id={`live:${day.date}:${groupKey(group)}`}
          group={{ ...group, expanded: true, match_count: rows.length }} rows={rows} onOpen={onOpen} />
      ))}
    </div>
  );
}

/** Якорь тура «Детали матча»: первая строка матча открытого дня, первая группа свёрнута — её заголовок. */
const FIRST_MATCH = 'list.matches.first';

const groupKey = (g: MatchGroup) => `${g.league?.id ?? 0}:${g.title}`;

/** Группы дня — серверные; в старом ответе без groups — по лигам в порядке строк, все раскрыты. */
export function groupsOf(day: MatchDay): MatchGroup[] {
  if (day.groups) return day.groups;
  const byLeague = new Map<number, MatchGroup>();
  for (const m of day.matches) {
    const id = m.league?.id ?? 0;
    if (!byLeague.has(id)) {
      byLeague.set(id, { title: m.league?.name ?? t('mc_matches'), league: m.league ?? null, match_ids: [], match_count: 0,
        live_count: 0, expanded: true, collapsible: true, is_favorite: false });
    }
    const g = byLeague.get(id)!;
    g.match_ids.push(m.id); g.match_count += 1; if (m.is_live) g.live_count += 1;
  }
  return [...byLeague.values()];
}

/** Строки группы по match_ids в порядке группы; id без строки (живые данные разошлись) — пропускаем. */
export function rowsOf(group: MatchGroup, day: MatchDay): MatchRow[] {
  const byId = new Map(day.matches.map((m) => [m.id, m]));
  return group.match_ids.map((id) => byId.get(id)).filter((m): m is MatchRow => Boolean(m));
}

export function GroupCard({ id, group, rows, onOpen, anchor }: {
  id: string; group: MatchGroup; rows: MatchRow[]; onOpen: (id: number) => void; anchor?: string;
}) {
  const [open, setOpen] = useState(() => !group.collapsible || (toggled.get(id) ?? group.expanded));
  const toggle = () => { if (!group.collapsible) return; haptic.selection(); toggled.set(id, !open); setOpen(!open); };
  const flag = group.league?.country?.image ?? null;
  return (
    <section className={styles.group}>
      <button className={`${styles.groupHeader} ${open && rows.length ? styles.groupHeaderOpen : ''}`} aria-expanded={open} onClick={toggle}
        disabled={!group.collapsible} {...anchorAttr(open && rows.length ? null : anchor)}>
        <span className={styles.groupIcon}>
          {/* флаг страны лиги — только если он есть (Figma Menu Cell 794:36288); у международных групп
              («World - ODI», «World - Tri-Series») слот пустой, логотип лиги не подставляем (HQ-1899) */}
          {flag && <img className={styles.flag} src={flag} alt="" />}
        </span>
        <span className={styles.groupName}>{group.title}</span>
        <span className={styles.groupRight}>
          <span className={styles.groupCount}>{group.match_count}</span>
          {group.collapsible && <span className={`${styles.caret} ${open ? styles.caretOpen : ''}`}><MaskIcon src={caretRight} size={20} /></span>}
        </span>
      </button>
      {open && rows.map((m, i) => <MatchRowView key={m.id} match={m} showLeague={false} meta={false} timeOnly context={group.league?.name} onOpen={onOpen}
        anchor={i === 0 ? anchor : undefined} />)}
    </section>
  );
}

/** Календарь по calendar[] полотна: один месяц сеткой с понедельника, листается ‹ ›; дни вне полотна неактивны,
 *  без матчей — бледные. Закрывается крестиком, тапом по фону, Escape и системной «Назад» Telegram. */
export function CalendarSheet({ calendar, active, onPick, onClose }: {
  calendar: CalendarDay[]; active: string; onPick: (d: string) => void; onClose: () => void;
}) {
  const months = useMemo(() => [...new Set(calendar.map((c) => c.date.slice(0, 7)))], [calendar]);
  const [index, setIndex] = useState(() => Math.max(0, months.indexOf(active.slice(0, 7))));
  const byDate = useMemo(() => new Map(calendar.map((c) => [c.date, c])), [calendar]);
  const weekdays = useMemo(() => Array.from({ length: 7 }, (_, i) =>
    formatDate(new Date(Date.UTC(2024, 0, 1 + i)), { weekday: 'narrow', timeZone: 'UTC' })), []);
  // окно поверх экрана: «Назад» закрывает календарь, кнопка экрана после — как была (HQ-1371)
  useOverlayDismiss(onClose);
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(ref);
  const month = months[index]!;
  const first = new Date(`${month}-01T00:00:00Z`);
  const offset = (first.getUTCDay() + 6) % 7;
  const length = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return (
    <div className={styles.sheetBackdrop} onClick={onClose}>
      <div ref={ref} className={styles.sheet} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className={styles.sheetHead}>
          <button className={styles.navBtn} aria-label={t('tg_prev_month')} disabled={index === 0} onClick={() => setIndex(index - 1)}><span aria-hidden="true">‹</span></button>
          <h3 className={styles.monthTitle}>{formatDate(first, { month: 'long', year: 'numeric', timeZone: 'UTC' })}</h3>
          <button className={styles.navBtn} aria-label={t('tg_next_month')} disabled={index === months.length - 1} onClick={() => setIndex(index + 1)}><span aria-hidden="true">›</span></button>
          <button className={styles.closeBtn} aria-label={t('mc_close')} onClick={onClose}>
            <MaskIcon src={closeIcon} size={20} />
          </button>
        </div>
        <div className={styles.grid}>
          {weekdays.map((w, i) => <span key={`w${i}`} className={styles.weekday}>{w}</span>)}
          {Array.from({ length: offset }, (_, i) => <span key={`o${i}`} />)}
          {Array.from({ length }, (_, i) => {
            const date = `${month}-${String(i + 1).padStart(2, '0')}`;
            const c = byDate.get(date);
            return (
              // диктору — полная дата и состояние, а не голая цифра: выбранный — aria-pressed, сегодня — aria-current (HQ-1401)
              <button key={date} disabled={!c} onClick={() => c && onPick(date)}
                aria-label={formatDate(new Date(`${date}T00:00:00Z`), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}
                aria-pressed={date === active} aria-current={c?.is_today ? 'date' : undefined}
                className={`${styles.cell} ${c && c.match_count > 0 ? styles.hasMatches : ''} ${date === active ? styles.cellActive : ''} ${c?.is_today ? styles.today : ''}`}>
                {i + 1}
                {c && c.live_count > 0 && <span className={styles.cellLive} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
